import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useGetAllTariffsQuery,
  useGetPaymentUrlMutation,
  useGetPriceCalculationQuery,
} from "@/services/api/entities/payment";
import { paymentActions, paymentSelectors, FlowType } from "@/features/payment-form/model";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { priceFormat } from "@/shared/utils";
import { CurrencyCode, type CalculatePricePayload } from "@/services/api/entities/payment/types";
import { appRouterPaths } from "@/services/navigation";
import { signUpActions, SignUpStep } from "@/features/sign-up";
import type { ChangeEvent } from "react";
import type { CheckboxOptionType, RadioChangeEvent } from "antd";
import { TARIFFS_EXTERNAL_ID } from "../constants";
import { getSafeLocalizedValue } from "../utils";

export const usePaymentForm = (flowType: FlowType) => {
  const [discountCode, setDiscountCode] = useState<string>("");

  const { i18n } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const selectedTariff = useAppSelector(paymentSelectors.selectSelectedTariff);
  const selectedTariffOptions = useAppSelector(paymentSelectors.selectSelectedTariffOptions);
  const priceCalculationPayload = useAppSelector(paymentSelectors.selectCalculatePricePayload);
  const getPaymentUrlPayload = useAppSelector(paymentSelectors.selectPaymentUrlPayload);
  const priceData = useAppSelector(paymentSelectors.selectPriceCalculation);

  const [getPaymentUrl] = useGetPaymentUrlMutation();
  const { data, isFetching } = useGetAllTariffsQuery(
    { externalId: TARIFFS_EXTERNAL_ID },
    { refetchOnMountOrArgChange: true },
  );
  const { isFetching: priceIsFetching } = useGetPriceCalculationQuery(
    priceCalculationPayload ?? ({} as CalculatePricePayload),
    {
      skip: !priceCalculationPayload,
      refetchOnMountOrArgChange: true,
    },
  );

  const fetchPaymentUrl = useCallback(() => {
    dispatch(paymentActions.setFlowType(flowType));

    if (!getPaymentUrlPayload) {
      return;
    }

    getPaymentUrl(getPaymentUrlPayload)
      .unwrap()
      .then((res) => {
        dispatch(paymentActions.setPaymentOperationId(res.paymentOperationId));
        dispatch(paymentActions.setIdempotenceKey(res.idempotenceKey));
        dispatch(paymentActions.setCheckPaymentPolling(true));

        if (flowType === FlowType.SIGN_UP) {
          dispatch(signUpActions.goToStep(SignUpStep.CheckPayment));
        } else {
          navigate(appRouterPaths.checkPayment);
        }

        window.open(res.paymentUrl, "_self");
      })
      .catch(() => {
        navigate(appRouterPaths.checkPayment);
      });
  }, [dispatch, getPaymentUrlPayload, getPaymentUrl, priceData]);

  const handleChangeRadio = useCallback(
    (event: RadioChangeEvent) => {
      const currentTariff = data?.find((tariff) => tariff.tariffPlanId === event.target.value);

      dispatch(paymentActions.setSelectedTariff(currentTariff ?? null));
    },
    [dispatch, data],
  );

  const handleChangeOptionItem = useCallback(
    (optionId: string, optionItemId: string) => {
      dispatch(paymentActions.updateSelectedTariffOption({ optionId, optionItemId }));
    },
    [dispatch],
  );

  const handleChangeDiscountCode = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(event.target.value);
  }, []);

  const handleApplyDiscountCode = useCallback(() => {
    dispatch(paymentActions.setDiscountCode(discountCode));
  }, [dispatch, discountCode]);

  const tariffsList: CheckboxOptionType[] = useMemo(() => {
    const dataForSort = data && [...data];
    return (
      dataForSort
        ?.sort((a, b) => {
          return a.type > b.type ? -1 : 1;
        })
        .map((tariff) => {
          return {
            label: getSafeLocalizedValue(tariff.localizedName, tariff.name),
            value: tariff.tariffPlanId,
          };
        }) ?? []
    );
  }, [data, i18n.language]);

  const price = useMemo(() => {
    return priceFormat({
      amount: priceData?.finalAmount ?? "",
      currencyCode: priceData?.currencyCode ?? CurrencyCode.RUR,
    });
  }, [priceData]);

  const discountHasError = priceCalculationPayload?.discount && !priceData?.discount;

  return {
    state: {
      isFetching,
      tariffsList,
      selectedTariff,
      selectedTariffOptions,
      price,
      priceIsFetching,
      discountCode,
      discountHasError,
    },
    methods: {
      handleChangeRadio,
      handleChangeOptionItem,
      handleChangeDiscountCode,
      handleApplyDiscountCode,
      fetchPaymentUrl,
    },
  };
};
